import { z } from 'zod';
import { ConfigComparisonSchema, ConfigFingerprintSchema, leverAxes, } from './config-fingerprint.js';
import { MemoryAnchorSetSchema } from './memory-anchors.js';
import { PersistentAgentInitialFactSchema, PersistentAgentWatchDeliverySchema, PutPersistentAgentWatchInputSchema, } from './persistent-agent.js';
import { ResolvedRunProfileSchema, RunProfileSchema } from './run-profile.js';
import { ScenarioRunManifestSchema } from './scenario-score.js';
import { PutTrialScorecardInputSchema, TrialMetricLineStatusSchema } from './trial-scorecard.js';
import { parseWatchSelector } from './watch-selector.js';
export const BenchmarkStatusSchema = z.enum([
    'IDLE',
    'PREPARING',
    'RUNNING',
    'COMPLETED',
    'TIMED_OUT',
    'FAILED',
    'CANCELLED',
]);
/**
 * Driver protocol the benchmark module uses to run/score trials.
 * - `benchmark_tool` (default): the MCP connection exposes a single `benchmark`
 *   tool with a `{command: list|start|status|cancel}` discriminant.
 * - `sim_scenario`: an external drone-sim world exposing the scenario tools
 *   (`list_scenarios` / `start_scenario` / `get_scenario_status` /
 *   `abort_scenario`) plus an HTTP-only scorecard at
 *   `{httpBaseUrl}/api/scenarios/runs/<run_id>`. Scoring stays HTTP-only so
 *   the agent can never read ground truth through its MCP toolset.
 */
export const BenchmarkDriverKindSchema = z.enum(['benchmark_tool', 'sim_scenario']);
/**
 * How a finished run should be read for aggregation (the "is this a real data
 * point?" axis, distinct from the pass/fail of the run itself):
 * - `ok`         — completed and every gate passed.
 * - `capability` — completed with a scorable result, but a gate failed or the
 *   score is low. A GENUINE data point (the agent underperformed) — counts.
 * - `infra`      — TIMED_OUT / FAILED / CANCELLED, or completed with no
 *   scorable metrics (the pre-#1006 stalled-inference class). This is MISSING
 *   DATA, not a zero: aggregation/pass^k must DROP it, never score it 0.
 */
export const RunOutcomeClassSchema = z.enum(['ok', 'capability', 'infra']);
/**
 * MCP tool names for the scenario-style driver. Defaults match the Formant
 * drone-sim scenario surface; override to point at any MCP that speaks a
 * similar list/start/status/abort lifecycle (cheap local stubs included).
 */
/** Partial tool-name overrides. Omitted keys fall back to drone-sim defaults at runtime. */
export const BenchmarkScenarioToolsSchema = z.object({
    list: z.string().min(1).optional(),
    /** Standing operator instructions for a scenario (blind — no schedule/GT). */
    briefing: z.string().min(1).optional(),
    start: z.string().min(1).optional(),
    status: z.string().min(1).optional(),
    abort: z.string().min(1).optional(),
});
export const BenchmarkScenarioProjectionSchema = z.enum([
    /** Per-event ground-truth scorecard (drone-sim /api/scenarios/runs shape). */
    'sim_events',
    /** No external score projection — kit metrics + LLM cost only. */
    'none',
]);
/**
 * classical-scripted dispatcher world contract + policy. Operator tools MUTATE
 * the world (the dispatcher acts); read tools OBSERVE it (state + open events).
 * All tool names optional → drone_fleet defaults at runtime. The event-source
 * read tool and the world-state field shapes are calibrated per world (the
 * external PoLB world may differ) — parsing stays defensive and must be
 * confirmed against a live world sample.
 */
export const ClassicalDispatchConfigSchema = z.object({
    /** Operator (world-mutating) tool names the dispatcher calls to act. */
    operatorTools: z
        .object({
        navigateToWaypoint: z.string().min(1).optional(),
        navigateToPose: z.string().min(1).optional(),
        captureImage: z.string().min(1).optional(),
        takeOff: z.string().min(1).optional(),
        /** Robot-level "block until motion settles" sync verb (drone_fleet: wait_for_drone). */
        waitForRobot: z.string().min(1).optional(),
        land: z.string().min(1).optional(),
        /** Optional explicit resolution/report verb; omitted → capture resolves. */
        report: z.string().min(1).optional(),
        /** Ground-Spot action verbs (drone_fleet: spot_walk_to / wait_for_spot / spot_take_photo / spot_undock). */
        spotWalkTo: z.string().min(1).optional(),
        spotWaitFor: z.string().min(1).optional(),
        spotCapture: z.string().min(1).optional(),
        spotUndock: z.string().min(1).optional(),
    })
        .optional(),
    /** Read (observation) tool names the dispatcher polls for state + events. */
    readTools: z
        .object({
        worldState: z.string().min(1).optional(),
        fleetState: z.string().min(1).optional(),
        droneState: z.string().min(1).optional(),
        /** Which read tool surfaces open events/alarms (default = worldState). */
        events: z.string().min(1).optional(),
    })
        .optional(),
    /** Arg key carrying the robot id on operator tools (e.g. 'drone'). Default 'drone'. */
    robotArgName: z.string().min(1).optional(),
    /** Arg key carrying the Spot id on spot_* tools. Default 'spot_id'. */
    spotArgName: z.string().min(1).optional(),
    /** Arg key carrying the event id on the report tool (e.g. 'event_id'). Default 'event_id'. */
    eventArgName: z.string().min(1).optional(),
    /** Max robots dispatched concurrently (back-pressure). Default 4. */
    maxConcurrentRobots: z.number().int().min(1).max(64).optional(),
    /** Bounded action steps per event (navigate → capture → report). Default 3. */
    perEventActionBudget: z.number().int().min(1).max(20).optional(),
    /** Dispatcher tick interval ms; default = pollIntervalSeconds * 1000. */
    tickMs: z.number().int().min(100).max(60_000).optional(),
    /** Objective (optimal/auction stacks): severity units paid per unit normalized
     *  response distance — the single utilitarian trade-off (default 0.5). */
    severityDistanceLambda: z.number().min(0).max(100).optional(),
    /** Robot travel speed (world units/sec) enabling the TTL feasibility gate
     *  (a robot that can't reach an event before its deadline isn't assigned). */
    robotSpeed: z.number().positive().optional(),
    /** Explicit pre-positioning staging point; omitted → centroid of open events. */
    stagingPosition: z.object({ x: z.number(), y: z.number(), z: z.number() }).optional(),
    /** Enable lull-time pre-positioning: idle-unassigned robots drift to staging. */
    prePosition: z.boolean().optional(),
    /**
     * Constant extra arguments merged into every call of a named operator tool,
     * keyed by the tool's resolved name. This exists for EXPERIMENTAL PARITY:
     * when the LLM stack's prompt gives its operator a tool hint the dispatcher
     * can't otherwise express (e.g. `navigate_to_pose({ route: 'direct' })` for
     * ground walkers), the same hint is granted to the classical stacks here so
     * the head-to-head isolates allocation intelligence, not tool-access. The
     * per-call computed args (robot id, x/y/z, event id) always win on a key
     * collision. Example: `{ "spot_walk_to": { "route": "direct" } }`.
     */
    operatorArgs: z.record(z.string(), z.record(z.string(), z.unknown())).optional(),
});
export const BenchmarkDriverConfigSchema = z.object({
    kind: BenchmarkDriverKindSchema.default('benchmark_tool'),
    /**
     * sim_scenario only: HTTP base of the world that serves the scorecard
     * (e.g. https://drone-sim.dev.formant.io/drone_fleet_daniel-world). When
     * omitted, derived from the MCP connection endpoint by stripping a trailing
     * /mcp or /sse path segment.
     */
    httpBaseUrl: z.string().url().optional(),
    /** Override MCP tool names (defaults = drone-sim scenario tools at runtime). */
    tools: BenchmarkScenarioToolsSchema.optional(),
    /**
     * Arg name for the scenario key on the start tool (default `name` at runtime →
     * start_scenario({ name: remoteBenchmarkKey })).
     */
    startScenarioArg: z.string().min(1).optional(),
    /**
     * Arg name for the per-run draw seed on the start tool (DEV-17328; default
     * `seed` at runtime). Only sent when the Start carried a scenarioSeed.
     */
    startSeedArg: z.string().min(1).optional(),
    /**
     * Arg name for the campaign trial index on the start tool (DEV-17327;
     * default `trial` at runtime). Per-trial seeds are opaque hashes, so
     * trial-indexed scenario rules (held-out kinds, null trials) need the
     * index sent explicitly. Always sent by the trial loop.
     */
    startTrialArg: z.string().min(1).optional(),
    /** Field on the start tool result that holds the remote run id (default run_id). */
    startRunIdField: z.string().min(1).optional(),
    /**
     * Status strings from the status tool that mean the run is done (success).
     * Runtime default: completed + aborted (sim scores aborted runs too).
     */
    completeStatuses: z.array(z.string().min(1)).min(1).optional(),
    /** Status strings that mean hard failure (runtime default failed/error). */
    failedStatuses: z.array(z.string().min(1)).optional(),
    /**
     * HTTP path template for the scorecard, relative to httpBaseUrl.
     * `{runId}` is substituted. Runtime default `/api/scenarios/runs/{runId}`.
     * With projection `none`, HTTP scoring is skipped.
     */
    scorecardPathTemplate: z.string().optional(),
    /**
     * How to turn the remote finish into BenchmarkResult rows.
     * Runtime default `sim_events`. Use `none` for cheap baby benches
     * (kit metrics + LLM cost only — no external ground-truth fetch).
     */
    projection: BenchmarkScenarioProjectionSchema.optional(),
    /**
     * When true (default), after start the driver calls the briefing tool and the
     * service injects any text into the trial wake. Set false to skip.
     */
    fetchBriefing: z.boolean().optional(),
    /**
     * classical-scripted stack only: how the deterministic dispatcher talks to
     * this world. Describes the WORLD contract (operator/read tool names) and
     * the fixed policy knobs — the same kind of config as `tools` above, but for
     * the participant that acts on the world rather than the scenario lifecycle.
     * Omitted keys fall back to drone_fleet defaults at runtime
     * (resolveClassicalDispatchOptions). Ignored by the LLM (`metaphysics-
     * orchestration`) stack, which drives its tools via the agent definition's
     * toolRefs, not this config.
     */
    classicalDispatch: ClassicalDispatchConfigSchema.optional(),
});
export const BenchmarkThreadSchema = z.object({
    name: z.string().min(1).max(200),
    initialFacts: z.array(PersistentAgentInitialFactSchema).max(50).default([]),
    /** Optional per-thread notification delivered when the benchmark starts. */
    initialNotification: z.string().min(1).max(20_000).optional(),
    /**
     * Situational anchors for this thread's MISSION WAKE (DEV-17326, §4.4):
     * whitelisted anchor keys (siteId, deviceId, robotClass, missionId, …)
     * merged into the wake event's attributes, so write-time anchor stamping
     * and anchored retrieval work during benchmark runs — previously the wake
     * carried only benchmarkRunId and ambient stamping was inert here. The
     * agent's static config.anchors still applies underneath; these are the
     * per-thread layer (this thread supervises THIS robot at THIS site).
     */
    anchors: MemoryAnchorSetSchema.optional(),
});
export const BenchmarkAgentStartupSchema = z.object({
    agentDefinitionId: z.string().min(1),
    threads: z.array(BenchmarkThreadSchema).min(1).max(50),
});
export const BenchmarkThreadStartupConfigSchema = z.object({
    /** Empty allowed on draft benches (kit shell); Start requires ≥1 agent/thread. */
    agents: z.array(BenchmarkAgentStartupSchema).max(100).default([]),
});
/** Stable result envelope; driver-specific fields belong in data and remain JSON-safe. */
export const BenchmarkResultSchema = z
    .object({
    key: z.string().min(1),
    type: z.string().min(1),
    label: z.string().min(1),
    passed: z.boolean().nullable().default(null),
    value: z.unknown().optional(),
    target: z
        .object({
        operator: z.enum(['eq', 'neq', 'lt', 'lte', 'gt', 'gte', 'contains']),
        value: z.unknown(),
        unit: z.string().optional(),
    })
        .optional(),
    summary: z.string().optional(),
    data: z.record(z.string(), z.unknown()).optional(),
    evidence: z.array(z.record(z.string(), z.unknown())).optional(),
})
    .passthrough();
export const BenchmarkLaunchedThreadSchema = z.object({
    persistentAgentId: z.string(),
    agentDefinitionId: z.string(),
    name: z.string(),
});
/** One scorecard metric line snapshot (multi-metric kits). */
export const BenchmarkScorecardLineSchema = z.object({
    id: z.string(),
    label: z.string().optional(),
    selector: z.string().optional(),
    lineStatus: TrialMetricLineStatusSchema,
    lastValue: z.number().nullable().optional(),
    target: z.number().optional(),
    comparator: z.string().optional(),
    baselineMode: z.enum(['absolute', 'delta']).optional(),
    baselineValue: z.number().nullable().optional(),
});
/**
 * Per-trial-thread kit snapshot when a benchmark run finishes.
 * Scorecard lines + normalized status (all-pass → met, finished unmet → failed).
 */
export const BenchmarkKitThreadMetricsSchema = z.object({
    persistentAgentId: z.string(),
    threadName: z.string().optional(),
    missionId: z.string().nullable(),
    status: z.enum(['active', 'met', 'failed', 'cancelled']).nullable(),
    objective: z.string().nullable(),
    finalValue: z.number().nullable(),
    stats: z
        .object({
        durationMs: z.number(),
        timeToTargetMs: z.number().nullable(),
        sampleCount: z.number().nullable(),
        costUsd: z.number(),
        turns: z.number(),
        toolCalls: z.number(),
        wakes: z.number(),
    })
        .nullable(),
    /** Multi-metric scorecard lines. */
    lines: z.array(BenchmarkScorecardLineSchema).optional(),
    scorecardStatus: z.enum(['active', 'completed', 'cancelled']).nullable().optional(),
});
/**
 * Sensors + scorecard done-condition stamped onto every thread a benchmark
 * launches. Watches are the same shape as PUT .../watches; scorecard is the
 * only grade path.
 */
export const BenchmarkKitWatchSchema = PutPersistentAgentWatchInputSchema.extend({
    /** Watch name on the trial thread (1..64). */
    name: z.string().min(1).max(64),
    /** Trials default to next-turn so clearance loops keep working while busy. */
    wakeDelivery: PersistentAgentWatchDeliverySchema.default('when-idle'),
}).superRefine((w, ctx) => {
    if (w.selector && parseWatchSelector(w.selector) === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Invalid selector '${w.selector}'`,
            path: ['selector'],
        });
    }
});
export const BenchmarkKitSchema = z.object({
    watches: z.array(BenchmarkKitWatchSchema).max(20).default([]),
    /** Multi-metric grade + sense hubs. */
    scorecard: PutTrialScorecardInputSchema.nullable().optional(),
});
/** Empty kit used when a benchmark has no sensors/done condition. */
export const emptyBenchmarkKit = () => ({
    watches: [],
    scorecard: null,
});
/** Coerce DB json (including legacy `{}`) into a validated kit. */
export function parseBenchmarkKit(raw) {
    if (raw == null ||
        (typeof raw === 'object' &&
            raw !== null &&
            !Array.isArray(raw) &&
            Object.keys(raw).length === 0)) {
        return emptyBenchmarkKit();
    }
    const parsed = BenchmarkKitSchema.safeParse(raw);
    return parsed.success ? parsed.data : emptyBenchmarkKit();
}
/** One execution of a Benchmark. Never overwritten in place — a new row per start. */
export const BenchmarkRunSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    benchmarkId: z.string(),
    orchestrationGroupId: z.string(),
    status: BenchmarkStatusSchema,
    /** Which agent stack ran this attempt — see knownStacks / stackDisplayName.
     *  Optional (not just nullable) so pre-stack-column run literals keep
     *  typechecking; the serializer always emits it (defaultStack for legacy). */
    stack: z.string().nullable().optional(),
    /** Derived (not stored): infra vs capability vs ok — see RunOutcomeClass.
     *  Lets aggregation drop infra runs as missing data. Optional so pre-field
     *  run literals keep typechecking; the serializer always emits it. */
    outcomeClass: RunOutcomeClassSchema.optional(),
    /** Optional operator label for this attempt ("code mode", "anthropic", …). */
    label: z.string().nullable(),
    /** Experiment arm this run belongs to; null = unarmed one-off.
     *  Optional (not just nullable) so pre-arms consumers' run literals keep
     *  typechecking — the serializer always emits it. */
    armId: z.string().nullable().optional(),
    /** 1-based trial number within the arm's Start request; null = pre-arms row. */
    trial: z.number().int().nullable().optional(),
    /** Cohort id shared by all trials of one Start launch (the reliability unit);
     *  null = legacy row / one-off import. Optional so pre-field run literals keep
     *  typechecking — the serializer always emits it. */
    runGroupId: z.string().nullable().optional(),
    /** Evidence-grade record of the configuration that actually ran. */
    resolvedProfile: ResolvedRunProfileSchema.nullable().optional(),
    /**
     * Derived (not stored): a content-addressed fingerprint of the config that
     * ran, for the one-lever-guarantee compare surface (DEV-17198). Computed from
     * `resolvedProfile` + run-level environment (stack, group); scenario/world/
     * sim-commit are folded in at the cross-benchmark compare layer. Null when a
     * run has no resolvedProfile (legacy/import). Optional so pre-field run
     * literals keep typechecking — the serializer always emits it when derivable.
     */
    configFingerprint: ConfigFingerprintSchema.nullable().optional(),
    launchedThreads: z.array(BenchmarkLaunchedThreadSchema),
    latestDriverResponse: z.unknown().nullable(),
    results: z.array(BenchmarkResultSchema),
    /** Scorecard snapshots from kit-armed trial threads (filled at run end). */
    kitMetrics: z.array(BenchmarkKitThreadMetricsSchema).default([]),
    rawResult: z.unknown().nullable(),
    /** OperatorStrategyAnalysis (or OperatorStrategyError) when the run was
     *  strategy-judged; null/absent otherwise. Kept loose here — producers
     *  validate with OperatorStrategyAnalysisSchema. */
    strategyAnalysis: z.unknown().nullable().optional(),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
    startedAt: z.date(),
    lastPolledAt: z.date().nullable(),
    finishedAt: z.date().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
/**
 * Compare the config fingerprints of N benchmark runs against the first
 * (baseline) — WITHIN one benchmark or ACROSS benchmarks (DEV-17198). The
 * enforcement point of the one-lever guarantee: given runs meant to isolate a
 * single lever, surface any comparison that actually moved more than one.
 */
export const CompareBenchmarkRunsInputSchema = z
    .object({
    /** Run ids to compare; the FIRST is the baseline every other diffs against. */
    runIds: z.array(z.string().min(1)).min(2).max(20),
    /** The single lever this set is meant to isolate — >1 changed lever ⇒ confounded. */
    isolatesLever: z.enum(leverAxes).optional(),
    /** True when these are trials of ONE arm — any differing config axis is a red flag. */
    sameArmTrials: z.boolean().optional(),
})
    .strict();
export const BenchmarkRunComparisonSchema = z.object({
    runId: z.string(),
    benchmarkId: z.string(),
    label: z.string().nullable(),
    stack: z.string().nullable(),
    /** This run's fingerprint (null when it captured no profile — legacy/import). */
    fingerprint: ConfigFingerprintSchema.nullable(),
    /** The comparison vs the baseline; null when either side has no fingerprint. */
    comparison: ConfigComparisonSchema.nullable(),
});
export const CompareBenchmarkRunsResultSchema = z.object({
    baselineRunId: z.string(),
    baselineFingerprint: ConfigFingerprintSchema.nullable(),
    comparisons: z.array(BenchmarkRunComparisonSchema),
});
export const BenchmarkSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    orchestrationGroupId: z.string(),
    mcpConnectionId: z.string(),
    remoteBenchmarkKey: z.string(),
    threadStartupConfig: BenchmarkThreadStartupConfigSchema,
    /** True when scenario id or trial threads are missing — Start is blocked. */
    runnable: z.boolean().optional(),
    /** Sensors + done-condition installed on each launched thread. */
    kit: BenchmarkKitSchema,
    driver: BenchmarkDriverConfigSchema,
    timeoutSeconds: z.number().int().positive(),
    pollIntervalSeconds: z.number().int().positive(),
    /** Model config used to strategy-judge completed runs; null = judging off.
     *  Optional (not just nullable) so pre-judge consumers' literals keep
     *  typechecking — the serializer always emits it. */
    judgeModelConfigId: z.string().nullable().optional(),
    /** Latest non-deleted run, if any have been started. Null = never run. */
    currentRun: BenchmarkRunSchema.nullable(),
    /** Second-latest non-deleted run — the baseline the current run's
     *  informational metrics (cost, turns, latency) compare against, since
     *  they have no absolute target. Null when fewer than two runs exist. */
    previousRun: BenchmarkRunSchema.nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const CreateBenchmarkInputSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    orchestrationGroupId: z.string().min(1),
    mcpConnectionId: z.string().min(1),
    /**
     * Driver scenario key. Empty = draft (save kit / config only).
     * Required non-empty to Start a run.
     */
    remoteBenchmarkKey: z.string().max(200).default(''),
    threadStartupConfig: BenchmarkThreadStartupConfigSchema.default({ agents: [] }),
    /** Optional; omitted/empty = no sensors or metric on trial start. */
    kit: BenchmarkKitSchema.optional(),
    /** Driver protocol config; omitted = legacy `benchmark` MCP tool contract. */
    driver: BenchmarkDriverConfigSchema.optional(),
    timeoutSeconds: z.number().int().min(10).max(86_400).default(1200),
    pollIntervalSeconds: z.number().int().min(1).max(300).default(5),
    /**
     * Opt-in operator-strategy judging: model config (org-scoped) used to
     * generate a stack-blind OperatorStrategyAnalysis after each completed
     * sim_scenario run. Omitted/null = no judging (no LLM cost).
     */
    judgeModelConfigId: z.string().min(1).nullable().optional(),
});
export const UpdateBenchmarkInputSchema = CreateBenchmarkInputSchema.partial();
/**
 * Cross-stack strategy judging: an arbitrary sim run dataset (the document
 * from `GET <world>/api/scenarios/runs/<run_id>`) to be judged by this
 * benchmark's judge model WITHOUT creating a run row. Lets non-platform
 * attempts (e.g. a code harness) be graded by the identical blind judge.
 */
export const AnalyzeBenchmarkDatasetInputSchema = z.object({
    dataset: z.record(z.string(), z.unknown()),
});
/**
 * Import a self-reported cross-stack attempt as a first-class (terminal)
 * BenchmarkRun — the mixed-stack leaderboard ingest. `dataset` is the sim run
 * scorecard the harness recorded (scored identically to a cortex run's
 * rawResult); `manifest` carries the self-reported identity — `stack`,
 * `window`, `profile`, and the self-reported `topology`. The score is computed
 * server-side from the dataset; the harness never reports its own score.
 */
export const ImportBenchmarkRunInputSchema = z.object({
    dataset: z.record(z.string(), z.unknown()),
    manifest: ScenarioRunManifestSchema,
});
export const StartBenchmarkInputSchema = z
    .object({
    /**
     * Optional label for the run this Start creates. Free text (trimmed);
     * empty/omitted = unlabeled. Not a controlled vocabulary — operators
     * tag attempts however they want ("code mode", "claude sonnet", …).
     */
    label: z.string().max(120).optional(),
    /**
     * Experiment-arm identifier. Runs sharing an armId ran the same
     * configuration — the compare/aggregation axis. Free slug ("mem-off",
     * "sonnet-baseline"); omitted = unarmed one-off run.
     */
    armId: z.string().min(1).max(120).optional(),
    /**
     * Which participant stack drives this run — the head-to-head axis. Omitted =
     * the native LLM persistent-agent stack (`metaphysics-orchestration`).
     * `classical-scripted` runs the deterministic non-LLM dispatcher against the
     * SAME sim + operator tools + scorecard (no PA threads launched). Only
     * launchable stacks are accepted here (see isLaunchableStack); harness
     * stacks (`claude-code`/`manual`) arrive via the cross-stack import path,
     * never a live Start.
     */
    stack: z.string().min(1).max(120).optional(),
    /**
     * Per-run overrides applied durably to every launched thread (see
     * RunProfileSchema for the durability contract). Omitted/{} = baseline.
     * Ignored by classical stacks (no LLM threads to override).
     */
    profile: RunProfileSchema.optional(),
    /**
     * Continue an EXISTING benchmark memory namespace (DEV-17254, §6.5)
     * instead of minting a fresh one — the "does the agent keep improving on
     * a corpus it built last week?" arm. Only valid when `profile.memoryMode`
     * is 'LIVE' on a thread-launching stack; must name a namespace in this
     * org. Omitted with 'LIVE' = the service mints a FRESH namespace for this
     * Start (the default: every launch begins from an empty store).
     */
    memoryNamespaceId: z.string().min(1).max(120).optional(),
    /**
     * Repeat count for this arm. Trials run SEQUENTIALLY (the partial unique
     * index allows one active run per orchestration group); each trial is its
     * own BenchmarkRun row sharing armId, numbered `trial` 1..N. The sequence
     * stops early if a trial FAILS or is CANCELLED.
     *
     * Memory carryover (DEV-17254): a 'LIVE' Start writes into ONE benchmark
     * namespace shared by all its trials, so trial N sees what trials 1..N-1
     * saved — the learning curve, by design — while production stays
     * untouched. For memory-independent trials use memoryMode 'OFF'.
     */
    trials: z.number().int().min(1).max(20).default(1),
    /**
     * Deterministic scenario draw seed (DEV-17328). Passed to the external
     * sim's start tool (sim_scenario driver; the arg name is driver config,
     * default `seed`) so ARM-PAIRED Starts face the same per-trial draw
     * sequence: two Starts with the same scenarioSeed and different memory
     * arms draw identical hidden-issue decks, making the memory lever the only
     * difference. Multi-trial Starts derive trial t's seed as
     * hash(scenarioSeed + ':' + t), so trials differ from each other but pair
     * across arms. Recorded on the run's resolvedProfile; deliberately NOT a
     * config-fingerprint axis (a draw seed is a sampling knob, not a config
     * lever — folding it in would mark same-arm trials as confounded).
     * Omitted = the sim's own default (falls back to its run id).
     */
    scenarioSeed: z.string().min(1).max(120).optional(),
    /**
     * How the seed maps onto trials (DEV-17328). 'per-trial' (default): trial
     * t draws from hash(scenarioSeed + ':' + t) — every trial faces a
     * DIFFERENT deck draw, but the sequence is reproducible and pairs across
     * arms whatever each Start's trial count (the learning-campaign mode:
     * exposure varies, luck doesn't). 'fixed': every trial sends scenarioSeed
     * verbatim — the SAME draw every trial (the repro/debugging mode). Only
     * valid with scenarioSeed.
     */
    scenarioSeedMode: z.enum(['per-trial', 'fixed']).optional(),
    /**
     * Campaign trial numbering base (DEV-17327/17260). A learning campaign
     * chains Starts (4-5 trials each) into one trial sequence; each Start's
     * internal numbering restarts at 1, so BOTH the per-trial seed
     * derivation and the sim's trial-indexed deck rules (held-out kinds,
     * null trials) would restart with it. trialOffset shifts the effective
     * index: trial t of this Start acts as campaign trial (trialOffset + t)
     * — seed hash(scenarioSeed + ':' + (trialOffset + t)) and the same
     * number forwarded to the sim's start tool. Default 0. Requires a
     * sim_scenario driver (like scenarioSeed).
     */
    trialOffset: z.number().int().min(0).max(10_000).optional(),
})
    .superRefine((v, ctx) => {
    if (v.scenarioSeedMode !== undefined && v.scenarioSeed === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['scenarioSeedMode'],
            message: 'scenarioSeedMode requires a scenarioSeed.',
        });
    }
});
/** PATCH body for renaming/labeling an existing run after the fact. */
export const UpdateBenchmarkRunInputSchema = z.object({
    /** Pass null or "" to clear. */
    label: z.string().max(120).nullable(),
});
/**
 * Reliability of one experiment arm across its trials — the honest head-to-head
 * metric ("reliability over mean"). Infra/missing-data trials (see
 * RunOutcomeClass) are EXCLUDED from n, not scored 0. `byK` holds the unbiased
 * pass@k / pass^k estimators (τ-bench / HumanEval form) per reporting horizon
 * k ≤ n; k=1 both reduce to passRate.
 */
/** One trial's contribution to a cohort's reliability (drives the drill-down). */
export const ReliabilityTrialSchema = z.object({
    runId: z.string(),
    /** 1-based trial index within the cohort; null on legacy/one-off rows. */
    trial: z.number().int().nullable(),
    /** Strict all-gates pass; null = infra (excluded from n). */
    passed: z.boolean().nullable(),
    /** Continuous [0,1] score (fraction of gates passed); null = infra / no gate. */
    score: z.number().nullable(),
    outcomeClass: RunOutcomeClassSchema,
    /** ISO start time (used to order the trials). */
    startedAt: z.string(),
});
export const ArmReliabilitySchema = z.object({
    /** Cohort id — one Start launch. Null groups legacy/unarmed runs by arm. */
    runGroupId: z.string().nullable(),
    /** Arm identifier; null = unarmed. */
    armId: z.string().nullable(),
    /** Operator label for the cohort (first run's label); display aid. */
    label: z.string().nullable(),
    /** Stack that ran the cohort (all its trials share it). */
    stack: z.string(),
    /** Trials counted (COMPLETED with a scorable result; infra excluded). */
    n: z.number().int().nonnegative(),
    /** Trials that passed every gate. */
    c: z.number().int().nonnegative(),
    /** Observed per-trial success rate c/n (0 when n = 0). */
    passRate: z.number(),
    /** Trials dropped as missing data (TIMED_OUT / FAILED / empty metrics). */
    infraExcluded: z.number().int().nonnegative(),
    /** Mean of the continuous per-trial score over counted trials; null if none. */
    meanScore: z.number().nullable(),
    /** Sample σ of the per-trial score — the run-to-run wobble (~2σ = MDE); null if n<2. */
    stdDev: z.number().nullable(),
    /** Mean event-response latency (sec) over trials carrying `mean_response_sec`;
     *  null if none report it. The head-to-head "how fast" column. */
    avgResponseSec: z.number().nullable(),
    /** Mean LLM cost (USD) over trials carrying `cost_usd_total`; null if none do
     *  (classical stacks launch no turns → no cost). The "how expensive" column. */
    avgCost: z.number().nullable(),
    /** pass@k / pass^k per reporting horizon k (string-keyed for JSON). */
    byK: z.record(z.string(), z.object({ passAtK: z.number(), passPowerK: z.number() })),
    /** Per-trial breakdown (ordered by startedAt) for the drill-down + curve. */
    trials: z.array(ReliabilityTrialSchema),
});
/** Per-arm reliability summary for a benchmark's runs (the E1 read surface). */
export const BenchmarkReliabilitySchema = z.object({
    benchmarkId: z.string(),
    arms: z.array(ArmReliabilitySchema),
});
export const BenchmarkCatalogItemSchema = z
    .object({
    key: z.string().min(1),
    name: z.string().optional(),
    description: z.string().optional(),
})
    .passthrough();
/**
 * Compact birds-eye replay packed from a sim 10 Hz trajectory JSONL.
 * Positions are meters; drone pose is [x, y, alt, status, battery].
 */
export const BenchmarkReplayEventSchema = z.object({
    id: z.string(),
    kind: z.string(),
    severity: z.string().nullable().optional(),
    near: z.string().nullable().optional(),
    x: z.number(),
    y: z.number(),
    z: z.number().nullable().optional(),
    at: z.number().nullable().optional(),
    spawned: z.number().nullable().optional(),
    ttl: z.number().nullable().optional(),
    outcome: z.string().nullable().optional(),
    resolved_at: z.number().nullable().optional(),
    resolved_by: z.string().nullable().optional(),
    response: z.number().nullable().optional(),
});
export const BenchmarkReplaySchema = z.object({
    run_id: z.string(),
    cortexRunId: z.string(),
    label: z.string(),
    scenario: z.string().nullable().optional(),
    scene: z.string().nullable().optional(),
    duration: z.number(),
    hz: z.number(),
    rollup: z.record(z.string(), z.unknown()).nullable().optional(),
    drones: z.array(z.string()),
    spots: z.array(z.string()),
    events: z.array(BenchmarkReplayEventSchema),
    frames: z.array(z.tuple([z.number(), z.array(z.array(z.number())), z.array(z.array(z.number()))])),
});
//# sourceMappingURL=benchmark.js.map